import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { Button } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { useEffect, useRef, useState } from "react";
import { type TableColumnProps } from "zhihao-ui-react";

import request from "@/api";
import { DEFAULT_VALUE } from "@/config";
import { AdminDetailTable } from "@/views/components/DetailTable";

export interface CustomerSubAccountRecord {
    account?: { id?: number; code?: string; realName?: string; mobile?: string };
    customerEnterprise?: { accountState?: number | string };
}

export interface CustomerSubAccountQuery {
    mainAccountId: number;
    page: number;
    size: number;
    state: number;
    sortKey: string;
    sortDirection: string;
}

export interface CustomerSubAccountPageProps {
    mainAccountId: number;
    loadSubAccounts?: (
        query: CustomerSubAccountQuery
    ) => Promise<{ list?: CustomerSubAccountRecord[]; total?: number }>;
    navigateTo?: (id: number) => void;
}

const accountStateLabels: Record<number, string> = { 1: "已激活", 2: "已失效" };
const DEFAULT_DETAIL_PAGE_SIZE = 20;

/** 复用 Vue `/admin/customer/sub/paging` 子账号分页接口和固定查询契约。 */
export async function loadCustomerSubAccounts(query: CustomerSubAccountQuery) {
    const response = await request.service.get("/admin/customer/sub/paging", { params: query });
    return response.data;
}

export function CustomerSubAccountPage({
    mainAccountId,
    loadSubAccounts = loadCustomerSubAccounts,
    navigateTo
}: CustomerSubAccountPageProps) {
    const [accounts, setAccounts] = useState<CustomerSubAccountRecord[]>([]);
    const [total, setTotal] = useState(0);
    const [errorMessage, setErrorMessage] = useState("");
    const [page, setPage] = useState(1);
    // ProTable 默认每页 20 条，详情内嵌列表沿用旧 Vue 默认分页大小。
    const [size, setSize] = useState(DEFAULT_DETAIL_PAGE_SIZE);
    const previousMainAccountId = useRef(mainAccountId);
    const query: CustomerSubAccountQuery = {
        mainAccountId,
        page,
        size,
        state: 2,
        sortKey: "created_time",
        sortDirection: "DESC"
    };

    // 用 zhihao-ui-react 列契约承接旧 Vue 子账号列表的字段和详情操作。
    const columns: TableColumnProps<CustomerSubAccountRecord>[] = [
        {
            key: "code",
            label: "用户ID",
            minWidth: 150,
            prop: "account",
            render: ({ row }) => (
                <Button
                    type="link"
                    aria-label={`查看 ${row.account?.code || row.account?.id}`}
                    onClick={() => openDetail(row.account?.id)}
                >
                    {row.account?.code || DEFAULT_VALUE}
                </Button>
            )
        },
        {
            key: "realName",
            label: "姓名",
            minWidth: 150,
            prop: "account",
            render: ({ row }) => row.account?.realName || DEFAULT_VALUE
        },
        {
            key: "mobile",
            label: "手机号",
            minWidth: 175,
            prop: "account",
            render: ({ row }) => row.account?.mobile || DEFAULT_VALUE
        },
        {
            key: "state",
            label: "状态",
            width: 92,
            prop: "customerEnterprise",
            render: ({ row }) => accountStateLabels[Number(row.customerEnterprise?.accountState)] || DEFAULT_VALUE
        }
    ];

    useEffect(() => {
        // 客户切换时先回到第一页，避免把旧客户的当前页带到新客户请求中。
        if (previousMainAccountId.current !== mainAccountId) {
            previousMainAccountId.current = mainAccountId;
            if (page !== 1) {
                setPage(1);
                return;
            }
        }

        let disposed = false;
        void loadSubAccounts(query)
            .then(result => {
                if (disposed) return;
                setAccounts(result.list || []);
                setTotal(result.total || 0);
                setErrorMessage("");
            })
            .catch(() => {
                if (!disposed) {
                    setAccounts([]);
                    setTotal(0);
                    setErrorMessage("子账号列表加载失败");
                }
            });
        return () => {
            disposed = true;
        };
    }, [loadSubAccounts, mainAccountId, page, size]);

    const openDetail = (id?: number) => {
        if (id == null) return;
        if (navigateTo) {
            navigateTo(id);
            return;
        }
        // 子账号详情遵循旧 Vue ProTable 的独立标签页行为，并使用部署时的 public path。
        const publicPath = import.meta.env.VITE_PUBLIC_PATH || "/";
        const normalizedPublicPath = publicPath.endsWith("/") ? publicPath : `${publicPath}/`;
        window.open(
            `${window.location.origin}${normalizedPublicPath}lianYunUser/userInfo?userId=${encodeURIComponent(id)}`,
            "_blank",
            "noopener,noreferrer"
        );
    };

    return (
        <section aria-label="customer-sub-account-page" className="section-card customer-sub-account-page">
            <h2 className="section-title">子账号列表</h2>
            <AdminDetailTable<CustomerSubAccountRecord>
                data={accounts}
                columns={columns}
                pagination={false}
                rowKey={row => String(row.account?.id || "")}
                isShowResetBtn={false}
                isShowSearchBtn={false}
                tableHeader={
                    <div className="admin-table-composed-header">
                        {errorMessage ? (
                            <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
                        ) : null}
                    </div>
                }
                append={
                    total > 0 ? (
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                pageSizeOptions={[10, 20, 30, 40, 50, 100]}
                                showSizeChanger
                                total={total}
                                hideOnSinglePage
                                onChange={(nextPage, nextSize) => {
                                    // 切换每页条数时回到第一页，保持旧 Vue ProTable 的分页行为。
                                    setPage(nextSize !== size ? 1 : nextPage);
                                    setSize(nextSize);
                                }}
                            />
                        </div>
                    ) : null
                }
            />
        </section>
    );
}
